import React, { useState, useMemo, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { addSymptoms } from '../../services/api/visitMetadataApi';
import { getSymptomSuggestions, COMMON_SYMPTOMS } from '../../constants/visitMetadata';

/**
 * AddSymptoms — Symptom selection based on visit doctor's specialization.
 *
 * Shows:
 *  1. Specialization-specific symptoms first (prominent)
 *  2. Common symptoms section
 *  3. Custom symptom input
 *
 * Selected symptoms appear as chips. User can toggle and add custom.
 */
export default function AddSymptoms({ visitId, doctorSpecialization, onSuccess, onClose }) {
  const { isDark } = useTheme();
  const [selected, setSelected] = useState(new Set());
  const [customSymptoms, setCustomSymptoms] = useState([]);
  const [customInput, setCustomInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Get symptom suggestions for this doctor's specialization
  const { common, specialized } = useMemo(
    () => getSymptomSuggestions(doctorSpecialization),
    [doctorSpecialization]
  );

  // Use fallback if no specialization match
  const hasSpecialized = specialized.length > 0;

  const toggleSymptom = useCallback((symptom) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(symptom)) {
        next.delete(symptom);
      } else {
        next.add(symptom);
      }
      return next;
    });
  }, []);

  const handleAddCustom = useCallback(() => {
    const trimmed = customInput.trim();
    if (!trimmed) return;
    // Avoid duplicates
    if (selected.has(trimmed) || customSymptoms.includes(trimmed)) return;
    setCustomSymptoms((prev) => [...prev, trimmed]);
    setSelected((prev) => new Set([...prev, trimmed]));
    setCustomInput('');
  }, [customInput, selected, customSymptoms]);

  const handleRemoveCustom = useCallback((symptom) => {
    setCustomSymptoms((prev) => prev.filter((s) => s !== symptom));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(symptom);
      return next;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddCustom();
      }
    },
    [handleAddCustom]
  );

  const handleSubmit = async () => {
    if (selected.size === 0) {
      setError('Please select at least one symptom');
      return;
    }

    const payload = [];
    // Standard symptoms
    const allStandard = new Set([...specialized, ...common, ...COMMON_SYMPTOMS]);
    for (const s of selected) {
      payload.push({
        name: s,
        is_custom: !allStandard.has(s),
      });
    }

    setSaving(true);
    setError(null);
    try {
      await addSymptoms(visitId, payload);
      setSelected(new Set());
      setCustomSymptoms([]);
      onSuccess?.('Symptoms recorded successfully');
    } catch (err) {
      setError(err.message || 'Failed to save symptoms');
    } finally {
      setSaving(false);
    }
  };

  const chipStyle = (isSelected) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.3rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.6875rem',
    fontWeight: isSelected ? 600 : 400,
    border: isSelected
      ? '1.5px solid #2563eb'
      : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    background: isSelected
      ? isDark ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.08)'
      : 'transparent',
    color: isSelected
      ? '#3b82f6'
      : isDark ? '#9ca3af' : '#6b7280',
    cursor: 'pointer',
    transition: 'all 0.15s',
    userSelect: 'none',
  });

  const inputBase = {
    padding: '0.5rem 0.625rem',
    borderRadius: '0.5rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.8125rem',
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>🤒</span>
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
            Add Symptoms
          </h3>
          {doctorSpecialization && (
            <span style={{
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.5625rem',
              fontWeight: 500,
              background: isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.08)',
              color: '#0d9488',
            }}>
              {doctorSpecialization}
            </span>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: isDark ? '#6b7280' : '#9ca3af' }}>✕</button>
        )}
      </div>

      {/* Selected count */}
      {selected.size > 0 && (
        <div style={{
          marginBottom: '0.625rem',
          padding: '0.375rem 0.625rem',
          borderRadius: '0.5rem',
          background: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.05)',
          border: `1px solid ${isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`,
          fontSize: '0.6875rem',
          color: '#22c55e',
          fontWeight: 500,
        }}>
          ✓ {selected.size} symptom{selected.size > 1 ? 's' : ''} selected
        </div>
      )}

      {/* Specialized Symptoms Section */}
      {hasSpecialized && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? '#6b7280' : '#9ca3af',
            marginBottom: '0.375rem',
          }}>
            For {doctorSpecialization || 'Specialist'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {specialized.map((symptom) => (
              <button
                key={symptom}
                onClick={() => toggleSymptom(symptom)}
                style={chipStyle(selected.has(symptom))}
              >
                {selected.has(symptom) && '✓ '}
                {symptom}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Common Symptoms Section */}
      {common.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? '#6b7280' : '#9ca3af',
            marginBottom: '0.375rem',
          }}>
            Common Symptoms
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {common.map((symptom) => (
              <button
                key={symptom}
                onClick={() => toggleSymptom(symptom)}
                style={chipStyle(selected.has(symptom))}
              >
                {selected.has(symptom) && '✓ '}
                {symptom}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fallback: show all common if no specialization */}
      {!hasSpecialized && common.length === 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? '#6b7280' : '#9ca3af',
            marginBottom: '0.375rem',
          }}>
            Common Symptoms
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {COMMON_SYMPTOMS.map((symptom) => (
              <button
                key={symptom}
                onClick={() => toggleSymptom(symptom)}
                style={chipStyle(selected.has(symptom))}
              >
                {selected.has(symptom) && '✓ '}
                {symptom}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Symptoms */}
      {customSymptoms.length > 0 && (
        <div style={{ marginBottom: '0.625rem' }}>
          <div style={{
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#0d9488',
            marginBottom: '0.375rem',
          }}>
            Custom Symptoms
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {customSymptoms.map((symptom) => (
              <span
                key={symptom}
                style={{
                  ...chipStyle(true),
                  borderColor: '#0d9488',
                  background: isDark ? 'rgba(13, 148, 136,0.15)' : 'rgba(13, 148, 136,0.08)',
                  color: '#0d9488',
                }}
              >
                {symptom}
                <button
                  onClick={() => handleRemoveCustom(symptom)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#0d9488',
                    fontSize: '0.625rem',
                    padding: 0,
                    marginLeft: '0.25rem',
                  }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add custom input */}
      <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
        <input
          type="text"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type custom symptom and press Enter..."
          style={{ ...inputBase, flex: 1 }}
        />
        <button
          onClick={handleAddCustom}
          disabled={!customInput.trim()}
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: customInput.trim() ? '#0d9488' : isDark ? 'rgba(13, 148, 136,0.2)' : 'rgba(13, 148, 136,0.15)',
            color: '#fff',
            fontSize: '0.6875rem',
            fontWeight: 600,
            cursor: customInput.trim() ? 'pointer' : 'not-allowed',
            opacity: customInput.trim() ? 1 : 0.5,
          }}
        >
          ＋ Add
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.5rem',
          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
          color: isDark ? '#fca5a5' : '#dc2626',
          fontSize: '0.75rem',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.75rem' }}>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              background: 'transparent',
              color: isDark ? '#9ca3af' : '#6b7280',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={saving || selected.size === 0}
          style={{
            padding: '0.5rem 1.25rem',
            borderRadius: '0.5rem',
            border: 'none',
            background: saving || selected.size === 0
              ? isDark ? 'rgba(59,130,246,0.3)' : 'rgba(37,99,235,0.3)'
              : 'linear-gradient(135deg, #2563eb, #3b82f6)',
            color: '#fff',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: saving || selected.size === 0 ? 'not-allowed' : 'pointer',
            opacity: saving || selected.size === 0 ? 0.5 : 1,
          }}
        >
          {saving ? 'Saving...' : `💾 Save ${selected.size} Symptom${selected.size !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}
